import { useCallback, useEffect, useState } from "react";
import { canFallBackToWebCheckout, isNativeApp, nativePlatform, storeLabel, waitForNativeBilling } from "@/lib/store-billing";
import { storeProductInfo } from "@/lib/native-billing";

export interface StorePrices {
  monthly: string | null;
  yearly: string | null;
  /** True when Apple/Google reported a free-trial introductory offer on monthly. */
  monthlyHasFreeTrial: boolean | null;
  loading: boolean;
  /** True inside the native wrapper — purchases must go through the store. */
  native: boolean;
  /** Set when StoreKit/Play could not return the monthly product. */
  error: string | null;
  reload: () => void;
}

/**
 * Localized store prices from StoreKit / Play Billing. Apple requires the
 * displayed price to come from the store (currency + regional pricing), so the
 * hard-coded USD copy is only a web/dev fallback.
 *
 * The hook waits for the Capacitor billing bridge to install, so a paywall
 * rendered before the plugin is ready still resolves to the native flow.
 */
export function useStorePrices(): Omit<StorePrices, "reload"> & { reload: () => void } {
  const native = isNativeApp();
  const [nonce, setNonce] = useState(0);
  const [state, setState] = useState({
    monthly: null as string | null,
    yearly: null as string | null,
    monthlyHasFreeTrial: null as boolean | null,
    loading: native,
    error: null as string | null,
  });

  useEffect(() => {
    if (!native) return;
    let active = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    void (async () => {
      const bridge = await waitForNativeBilling();
      if (!active) return;
      if (!bridge) {
        setState((s) => ({
          ...s,
          loading: false,
          // No bridge but web checkout is still possible (Android/PWA wrapper):
          // stay silent so the paywall can fall back instead of showing an error.
          error: canFallBackToWebCheckout()
            ? null
            : `We couldn't reach the ${storeLabel(nativePlatform())} right now. Please check your connection and try again.`,
        }));
        return;
      }
      const [m, y] = await Promise.all([storeProductInfo("monthly"), storeProductInfo("yearly")]);
      if (!active) return;
      setState({
        monthly: m?.displayPrice ?? null,
        yearly: y?.displayPrice ?? null,
        monthlyHasFreeTrial: m?.introOffer ? Boolean(m.introOffer.isFreeTrial) : null,
        loading: false,
        error: m?.displayPrice
          ? null
          : `Subscription details couldn't be loaded from the ${storeLabel(nativePlatform())}. Please try again in a moment.`,
      });
    })();
    return () => {
      active = false;
    };
  }, [native, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { ...state, native, reload };
}
